// Per spec/blocks.md §text (P-037.28 / D-136). One of 4 atomic Blocks
// that replace the prior `commentary` + `text-block` Blocks (dropped at
// the v9 → v10 migration). Renders a richText body sanitized via
// DOMPurify (D-022).
//
// **Body typography matches the pre-D-136 text-block body:** 14 px,
// text-primary, left-aligned. The atomic split lets staff compose
// "letter"-style emails freely. Tiptap's BulletList + OrderedList
// extensions are enabled project-wide at D-136 — bulleted + numbered
// lists in this body flow through DOMPurify's extended allow-list
// (`ul + ol + li`) intact.
//
// **Outer cell padding: 30 px L/R + 0 top.** Same content-gutter
// convention as `headline` + `button` + `author` so the 4 atomic Blocks
// compose cleanly. Vertical rhythm between Blocks is the document-shell's
// between-block rhythm.
//
// **Auto-hide on empty `body`.** Required at the Zod layer (`min(1)` on
// the source string); defense-in-depth if persisted state slips through
// empty. The Tiptap editor emits `<p></p>` (a single empty paragraph)
// when cleared, which is a 7-char non-empty string at the persistence
// layer — `min(1)` accepts it. The auto-hide runs on the unsanitized
// source string, so the empty-string path covers the "nothing typed yet"
// case. A persisted `<p></p>` still renders (visually empty) — staff can
// either delete the Block or accept the blank row.
//
// **Export name `textBlock` rather than `text`** to avoid a collision
// with the `text()` primitive (`src/primitives/text.ts`). The Block ID
// `'text'` stays canonical at the registry layer.
//
// **P-037.29 post-Phase-Close §item F (Banks's text-Block top-padding
// fix).** Body now flows through `richTextBody()` instead of the
// `text()` primitive. The previous wrapper produced `<p style="…"><p>…
// </p></p>` — HTML5 closes the outer `<p>` when the inner opens, so
// the outer styling was wasted and the body inherited UA-default font /
// 1em top margin (≈14–16 px of phantom top padding). `richTextBody()`
// injects per-`<p>`/`<ul>`/`<ol>` inline styling directly, with
// first-block `margin: 0` and 14 px paragraph-spacing on subsequent
// top-level blocks. See `src/render/rich-text-body.ts` for the rationale.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { richTextBody } from '../render/rich-text-body'
import { sanitizeRichText } from '../render/sanitize'

const optionsSchema = z.object({})

const inputsSchema = z.object({
  body: z.string().min(1).describe('richText'),
})

const BODY_SIZE_PX = 14

export const textBlock = defineBlock({
  id: 'text',
  label: 'Text',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { body: '<p>Add your content here.</p>' },
  autoHide: ({ inputs }) => inputs.body.length === 0,
  render: ({ inputs }) => {
    const bodyHtml = richTextBody(sanitizeRichText(inputs.body), { size: BODY_SIZE_PX })
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${bodyHtml}</td></tr></table>`
  },
})
